import { formatArea, formatLength } from '../../lib/geometryMetrics';
import { useMapContext } from '../../map/MapContext';

/** The map-toolbar "Объектный калькулятор" tool's results panel — distinct from the
 * per-object metrics toggle in SelectedObjectCard/ObjectCardModal, this one accumulates
 * a running tally across several same-type objects clicked one at a time (count, plus
 * summed length or area depending on the locked layer's geometry).
 *
 * Prototype feature — see src/lib/featureFlags.ts (objectCalculatorOnMap). No flag
 * check needed in this file itself: it renders nothing while objectCalcActive is
 * false, and that can only ever become true via MapControls' button, which is where
 * the flag is actually checked. */
export function ObjectCalculatorPanel() {
  const { objectCalcActive, objectCalcItems, objectCalcTotals, stopObjectCalc, clearObjectCalc } = useMapContext();

  if (!objectCalcActive) return null;

  const layerName = objectCalcItems[0]?.layerName;

  return (
    <div className="absolute bottom-6 left-6 z-10 flex w-[min(340px,calc(100vw-48px))] flex-col gap-3 rounded-2xl border border-border-default bg-bg-surface p-5 shadow-lg">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-text-primary">Объектный калькулятор</p>
          <p className="mt-0.5 text-[11px] text-text-secondary">
            {layerName ? `Слой: ${layerName}` : 'Кликните по объекту на карте, чтобы начать'}
          </p>
        </div>
        <button
          type="button"
          onClick={stopObjectCalc}
          title="Завершить"
          className="flex size-7 shrink-0 items-center justify-center rounded-lg text-text-secondary hover:bg-bg-surface-secondary"
        >
          ✕
        </button>
      </div>

      <div className="flex items-center gap-4 rounded-xl bg-bg-surface-secondary px-4 py-3">
        <div>
          <p className="text-[11px] text-text-secondary">Объектов</p>
          <p className="text-lg font-semibold text-text-primary">{objectCalcTotals.count}</p>
        </div>
        {objectCalcTotals.lengthM !== null && (
          <div>
            <p className="text-[11px] text-text-secondary">Суммарная длина</p>
            <p className="text-lg font-semibold text-text-primary">{formatLength(objectCalcTotals.lengthM)}</p>
          </div>
        )}
        {objectCalcTotals.areaM2 !== null && (
          <div>
            <p className="text-[11px] text-text-secondary">Суммарная площадь</p>
            <p className="text-lg font-semibold text-text-primary">{formatArea(objectCalcTotals.areaM2)}</p>
          </div>
        )}
      </div>

      {objectCalcItems.length > 0 && (
        <div className="flex max-h-40 flex-col gap-1 overflow-y-auto">
          {objectCalcItems.map((item, i) => (
            <div key={item.id} className="flex items-center justify-between gap-2 text-[11px]">
              <span className="truncate text-text-primary">
                {i + 1}. {item.title}
              </span>
              <span className="shrink-0 text-text-secondary">
                {item.metrics?.kind === 'line'
                  ? formatLength(item.metrics.lengthM)
                  : item.metrics?.kind === 'polygon'
                    ? formatArea(item.metrics.areaM2)
                    : ''}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2">
        <p className="flex-1 text-[11px] text-text-secondary">
          {layerName ? 'Клик по тому же объекту убирает его из подсчёта.' : 'Считаются только объекты одного слоя.'}
        </p>
        {objectCalcItems.length > 0 && (
          <button
            type="button"
            onClick={clearObjectCalc}
            className="shrink-0 text-[11px] font-semibold text-text-secondary hover:text-text-primary"
          >
            Очистить
          </button>
        )}
      </div>
    </div>
  );
}
